/**
 * Action Registry — single source of truth for each spreadsheet action type:
 * its field validation, its Excel side effect, and its result message.
 *
 * actionParser.ts and actionExecutor.ts both dispatch through this registry
 * so adding an action type means adding one entry here, not three switches.
 *
 * `promptDoc` is also the source for the "Available actions" section of
 * agent-instructions.md — see scripts/generate-action-docs.ts.
 */

import type {
  CreateChartAction,
  CreateSheetAction,
  FilterDataAction,
  FormatCellsAction,
  FormatChartAction,
  RunScriptAction,
  SortDataAction,
  SpreadsheetAction,
  ToggleGridlinesAction,
  WriteDataAction,
  WriteFormulaAction,
} from "./actionParser";

import {
  applyFilter,
  applySort,
  createChart,
  createSheet,
  formatCells,
  formatChart,
  insertFormula,
  runScript,
  serializeScriptResult,
  summarizeScriptResult,
  toggleGridlines,
  writeToRange,
} from "./excelContext";

export interface ActionDefinition<T extends SpreadsheetAction, R = void> {
  /** Structural field check — not a type predicate, since the concrete
   * action interfaces have no index signature to satisfy one. */
  validate(action: Record<string, unknown>): boolean;
  execute(action: T): Promise<R>;
  /** Receives execute()'s return value too, so e.g. run_script can report its result. */
  describe(action: T, result: R): string;
  /**
   * Extra context for the model only, kept out of what the user reads —
   * run_script uses it to hand back the script's full returned value.
   */
  detail?(action: T, result: R): string | undefined;
  /** Markdown snippet (heading, example, field notes) rendered into agent-instructions.md. */
  promptDoc: string;
}

type ActionRegistry = {
  [K in SpreadsheetAction["type"]]: ActionDefinition<
    Extract<SpreadsheetAction, {type: K}>,
    // Each action's execute() return shape differs (most return void, run_script
    // returns its script's result); actionExecutor narrows this with its own cast.
    unknown
  >;
};

export const actionRegistry: ActionRegistry = {
  write_data: {
    validate: (a) => typeof a.startCell === "string" && Array.isArray(a.values),
    execute: async (action) => {
      await writeToRange(action.startCell, action.values);
    },
    describe: (action) => {
      const rows = action.values.length;
      const cols = action.values[0]?.length ?? 0;
      return `Wrote ${rows}×${cols} data block starting at ${action.startCell}.`;
    },
    promptDoc: `### Write data to cells

\`\`\`action
{ "type": "write_data", "startCell": "B4", "values": [[10]] }
\`\`\`

- startCell: top-left cell (e.g. "B4", "A1")
- values: 2D array. Single cell: [[10]], one row: [["Alice", 30]], multiple rows: [["Alice", 30], ["Bob", 25]]`,
  },

  write_formula: {
    validate: (a) =>
      typeof a.cell === "string" && typeof a.formula === "string",
    execute: async (action) => {
      await insertFormula(action.cell, action.formula);
    },
    describe: (action) =>
      `Inserted formula ${action.formula} in cell ${action.cell}.`,
    promptDoc: `### Write a formula

\`\`\`action
{ "type": "write_formula", "cell": "E2", "formula": "=SUM(A2,D2)" }
\`\`\`

- IMPORTANT: In the JSON action block, always use **commas** (\`,\`) as the formula argument separator, regardless of the user's locale. The add-in normalizes formulas to English locale before executing them in Excel. The user's actual locale is provided in the context so you can respond in their language.
- CORRECTING formulas: the context lists existing formulas under a \`Formulas:\` block (e.g. \`E2: =A2/B2\`). The CSV shows only their RESULTS, including errors like \`#DIV/0!\`, \`#REF!\`, \`#VALUE!\`, \`#N/A\`. When asked to fix a formula, read the actual formula text from the \`Formulas:\` block, diagnose the problem, and emit a corrected \`write_formula\` for that cell (explain what was wrong).`,
  },

  create_chart: {
    validate: (a) =>
      typeof a.chartType === "string" && typeof a.dataRange === "string",
    execute: async (action) => {
      await createChart({
        type: action.chartType,
        dataRange: action.dataRange,
        title: action.title,
        position: action.position,
        colorScheme: action.colorScheme,
        dataLabels: action.dataLabels,
        legend: action.legend,
        axisTitles: action.axisTitles,
      });
    },
    describe: (action) =>
      `Created ${action.chartType} chart from ${action.dataRange}${action.title ? ` titled "${action.title}"` : ""}${action.position ? ` at ${action.position}` : ""}.`,
    promptDoc: `### Create a chart

\`\`\`action
{ "type": "create_chart", "chartType": "bar", "dataRange": "A1:B5", "title": "My Chart", "position": "G1:O15", "colorScheme": "blue", "dataLabels": true, "legend": { "position": "bottom" }, "axisTitles": { "x": "Month", "y": "Revenue (€)" } }
\`\`\`

- chartType: "bar", "column", "line", "pie", "area", "scatter", or "doughnut"
- dataRange: must be a single **contiguous** range (e.g. "A1:B5"). Non-contiguous ranges like "A1:A5,C1:C5" are NOT supported. If the columns you need are not adjacent, first use write_data to copy the relevant data into adjacent columns on a helper area or new sheet, then chart from there.
- position: WHERE to place the chart, as a range "topLeft:bottomRight" (e.g. "G1:O15") or a single top-left anchor cell (e.g. "G1"). YOU are responsible for choosing the position. BEFORE choosing, ANALYZE the sheet layout in the context: the \`[Used data range: ...]\` marker tells you the cells the data occupies, and the \`[Existing charts (approx. cell area ...): ...]\` marker lists the cell area each existing chart already covers. Pick an EMPTY area that does NOT overlap the used data range OR any existing chart. A chart spans roughly 8 columns × 15 rows, so leave that much room. When creating MULTIPLE charts, give each one a DIFFERENT position so they do NOT stack on top of each other — e.g. first at "G1:O15", second at "G17:O31", third at "G33:O47". Prefer placing charts to the RIGHT of, or BELOW, the used data range and clear of every existing chart.
- STYLE — choose these deliberately every time, don't leave Excel's flat default. Only skip them if the user explicitly wants the plain Excel look:
  - colorScheme: "colorful" (varied hues, good default for categorical data), "blue", "green", "orange" (single-hue gradients, good for sequential/ordered data), or "grayscale" (print-friendly, neutral)
  - dataLabels: true to show the value on each bar/slice/point (usually a good idea unless the chart is dense with many points, e.g. a line chart with many dates)
  - legend: { "position": "top"|"bottom"|"left"|"right", "visible": true|false } — omit "visible" to show it, set false to hide it (e.g. for a single-series chart where it's redundant)
  - axisTitles: { "x": "...", "y": "..." } — label both axes for bar/column/line/area/scatter charts so the chart is self-explanatory without reading the surrounding sheet (not applicable to pie/doughnut)`,
  },

  format_chart: {
    validate: (a) => typeof a.chartName === "string",
    execute: async (action) => {
      await formatChart({
        chartName: action.chartName,
        sheetName: action.sheetName,
        title: action.title,
        colorScheme: action.colorScheme,
        dataLabels: action.dataLabels,
        legend: action.legend,
        axisTitles: action.axisTitles,
      });
    },
    describe: (action) => `Restyled chart "${action.chartName}".`,
    promptDoc: `### Restyle an existing chart

\`\`\`action
{ "type": "format_chart", "chartName": "Chart 1", "colorScheme": "blue", "dataLabels": true, "legend": { "position": "bottom" }, "axisTitles": { "x": "Month", "y": "Revenue (€)" } }
\`\`\`

Use this to improve the look of a chart that ALREADY EXISTS — do NOT reach for \`run_script\` to restyle a chart; the Office.js chart formatting API (e.g. \`chart.chartArea\`, manual per-series colouring) is easy to get wrong and fails hard. This action wraps the correct, tested calls.

- chartName: the chart's name, e.g. "Chart 1" — read it from the \`[Existing charts ...]\` sheet layout marker, or from a \`run_script\` that lists \`sheet.charts.items.map(c => c.name)\`
- sheetName: optional, only needed if the chart is not on the active sheet
- Same style fields as \`create_chart\`: title, colorScheme, dataLabels, legend, axisTitles — see above for allowed values`,
  },

  sort_data: {
    validate: (a) =>
      typeof a.range === "string" &&
      typeof a.column === "number" &&
      typeof a.ascending === "boolean",
    execute: async (action) => {
      await applySort(action.range, action.column, action.ascending);
    },
    describe: (action) =>
      `Sorted ${action.range} by column ${action.column} ${action.ascending ? "ascending" : "descending"}.`,
    promptDoc: `### Sort data

\`\`\`action
{ "type": "sort_data", "range": "A1:D10", "column": 1, "ascending": false }
\`\`\`

- column: 0-based column index within the range`,
  },

  filter_data: {
    validate: (a) =>
      typeof a.range === "string" &&
      typeof a.column === "number" &&
      typeof a.criteria === "string",
    execute: async (action) => {
      await applyFilter(action.range, action.column, action.criteria);
    },
    describe: (action) =>
      `Filtered ${action.range} column ${action.column} with criteria "${action.criteria}".`,
    promptDoc: `### Filter data

\`\`\`action
{ "type": "filter_data", "range": "A1:D10", "column": 1, "criteria": ">100" }
\`\`\``,
  },

  format_cells: {
    validate: (a) => typeof a.range === "string",
    execute: async (action) => {
      await formatCells({
        range: action.range,
        fillColor: action.fillColor,
        fontColor: action.fontColor,
        bold: action.bold,
        italic: action.italic,
        fontSize: action.fontSize,
        numberFormat: action.numberFormat,
      });
    },
    describe: (action) => {
      const parts: string[] = [];
      if (action.fillColor) parts.push(`fill=${action.fillColor}`);
      if (action.fontColor) parts.push(`font=${action.fontColor}`);
      if (action.bold !== undefined) parts.push(`bold=${action.bold}`);
      if (action.italic !== undefined) parts.push(`italic=${action.italic}`);
      if (action.fontSize) parts.push(`size=${action.fontSize}`);
      if (action.numberFormat)
        parts.push(`numberFormat=${action.numberFormat}`);
      return `Formatted ${action.range}: ${parts.join(", ")}.`;
    },
    promptDoc: `### Format / paint cells

\`\`\`action
{ "type": "format_cells", "range": "A1:B5", "fillColor": "#FFFF00", "fontColor": "#FF0000", "bold": true, "italic": false, "fontSize": 14 }
\`\`\`

Example — normalize a column to euro currency and a column to dates:

\`\`\`action
[
  { "type": "format_cells", "range": "C2:C50", "numberFormat": "#,##0.00 \\"€\\"" },
  { "type": "format_cells", "range": "D2:D50", "numberFormat": "dd/mm/yyyy" }
]
\`\`\`

- range: cell range to format (e.g. "A1:B5", "C3")
- fillColor: background colour as hex (optional)
- fontColor: text colour as hex (optional)
- bold: true/false (optional)
- italic: true/false (optional)
- fontSize: number in points (optional)
- numberFormat: Excel number format code applied to the whole range (optional). ALWAYS write it with English/invariant tokens (\`.\` as decimal, \`,\` as thousands) — Excel renders it in the user's locale automatically. Examples: "#,##0.00" (2 decimals), "#,##0" (thousands separator), "#,##0.00 \\"€\\"" (euro), "$#,##0.00" (dollar), "0%" or "0.0%" (percent), "dd/mm/yyyy" (date), "hh:mm" (time), "@" (force text). Use numberFormat to normalize how dates, currency, and numbers are DISPLAYED without changing the underlying values.
- You can combine any subset of these properties.`,
  },

  toggle_gridlines: {
    validate: (a) => typeof a.show === "boolean",
    execute: async (action) => {
      await toggleGridlines(action.show);
    },
    describe: (action) => `Gridlines ${action.show ? "shown" : "hidden"}.`,
    promptDoc: `### Toggle gridlines

\`\`\`action
{ "type": "toggle_gridlines", "show": false }
\`\`\`

- show: true to display gridlines, false to hide them`,
  },

  create_sheet: {
    validate: (a) =>
      typeof a.name === "string" && (a.name as string).length > 0,
    execute: async (action) => {
      await createSheet(action.name);
    },
    describe: (action) => `Created new sheet "${action.name}".`,
    promptDoc: `### Create a new sheet

\`\`\`action
{ "type": "create_sheet", "name": "Summary" }
\`\`\`

- name: the name for the new worksheet`,
  },

  run_script: {
    validate: (a) =>
      typeof a.code === "string" &&
      (a.code as string).trim().length > 0 &&
      typeof a.description === "string" &&
      (a.description as string).trim().length > 0 &&
      (a.confirmMessage === undefined || typeof a.confirmMessage === "string"),
    execute: async (action) => {
      const returnValue = await runScript(action.code);
      return {returnValue};
    },
    describe: (action, result) => {
      const summary = summarizeScriptResult(
        (result as {returnValue: unknown} | undefined)?.returnValue,
      );
      return summary
        ? `Ran script: ${action.description} (${summary}).`
        : `Ran script: ${action.description}.`;
    },
    detail: (_action, result) => {
      const serialized = serializeScriptResult(
        (result as {returnValue: unknown} | undefined)?.returnValue,
      );
      return serialized ? `Script returned:\n${serialized}` : undefined;
    },
    promptDoc: `### Run a script (escape hatch)

Use this ONLY when none of the typed actions above can do what's needed — merging cells, freezing panes, conditional formatting, inserting/deleting rows or columns, renaming a sheet, autofit, data validation, and anything else outside the registry. PREFER a typed action when one fits: it's shorter to emit and gives the user a clearer summary (e.g. "Created bar chart 'Sales'" instead of "Ran script").

- **Chart formatting belongs to \`create_chart\`/\`format_chart\`, never \`run_script\`.** The chart-area format property is \`chart.format\` (fill/border/font) — there is NO \`chart.chartArea\`. Referencing it throws "Cannot read properties of undefined (reading 'format')" and fails the whole script. Restyling colours, titles, data labels, legend, or axis titles on an existing chart is \`format_chart\`'s job.

\`\`\`action
{ "type": "run_script", "code": "const sheet = context.workbook.worksheets.getActiveWorksheet(); sheet.freezePanes.freezeRows(1); await context.sync();", "description": "Freeze the header row" }
\`\`\`

- code: the BODY of an async function that receives \`context\` — an Excel.RequestContext already inside an active Excel.run batch. Call \`await context.sync()\` yourself before reading any loaded property, and again at the end if you changed anything.
- description: a short human-readable summary of what the script does, shown to the user INSTEAD of the code.
- confirmMessage: REQUIRED whenever the script deletes, clears, or overwrites a populated range or sheet. A plain-English question the user must confirm before the script runs (e.g. "This will delete the sheet 'Q3 Data'. Continue?") — do not mention code. Omit this field entirely for non-destructive scripts. Note: the add-in also detects common destructive patterns (sheet deletion, range clearing, value overwrites) automatically as a backstop, but you should still supply your own confirmMessage — it can describe the effect more precisely than the automatic fallback.
- If the script throws, you'll be told it failed and why — try a different approach rather than repeating the same script.

#### Querying the workbook with a script

A script may \`return\` a JSON-serialisable value. Whatever it returns comes back to you on the next iteration, so use a script to ASK the workbook a question whenever the CSV context cannot answer it — because the sheet is bigger than the context size cap, or because the fact you need never appears in a CSV at all (cell formatting, chart properties, row counts, defined names, what a filter is hiding, conditional formatting rules).

\`\`\`action
{ "type": "run_script", "code": "const sheet = context.workbook.worksheets.getActiveWorksheet(); const range = sheet.getUsedRange(); range.load(\\"rowCount,columnCount\\"); await context.sync(); const data = sheet.getRange(\`A2:C\${range.rowCount}\`); data.load(\\"values\\"); await context.sync(); const totals = {}; for (const [rep, , amount] of data.values) { totals[rep] = (totals[rep] || 0) + Number(amount); } return totals;", "description": "Total sales per rep across the whole sheet" }
\`\`\`

- Do the FILTERING and AGGREGATION inside the script and return only the answer. Returned values are size-capped: anything over the cap comes back truncated, and you will be told so explicitly — if that happens, narrow the query rather than re-running it unchanged.
- DATES: \`range.values\` gives dates as Excel serial numbers, which are meaningless on their own. Load \`text\` alongside \`values\` and return the text when you need a readable date, or convert the serial yourself (\`new Date(Date.UTC(1899, 11, 30 + serial))\`).
- ERROR VALUES: cells holding errors come back as the strings \`#DIV/0!\`, \`#REF!\`, \`#VALUE!\`, \`#N/A\` — return them as-is; they survive the round trip and tell you what is broken.
- The user sees only a short summary of what came back (e.g. "returned 12 rows × 3 columns"), so state the actual answer in your reply.`,
  },
};
